import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import {
  doc, getDoc, collection, getDocs, addDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconCheck,
  IconPlus,
  IconTrash,
  IconGripVertical,
  IconChevronDown,
  IconChevronRight,
  IconX,
} from '@tabler/icons-react';

const CATEGORIES = [
  { value: 'agricultural', labelKey: 'cat.agricultural' },
  { value: 'groundcare',   labelKey: 'cat.groundcare'   },
  { value: 'plant',        labelKey: 'cat.plant'        },
  { value: 'vehicle',      labelKey: 'cat.vehicle'      },
  { value: 'atv',          labelKey: 'cat.atv'          },
];

const SCOPE_OPTIONS = [
  { value: 'all',      labelKey: 'checks.scope_all',      descriptionKey: 'tmpl.scope_all_desc'      },
  { value: 'category', labelKey: 'tmpl.scope_category',   descriptionKey: 'tmpl.scope_category_desc' },
  { value: 'specific', labelKey: 'checks.scope_specific', descriptionKey: 'tmpl.scope_specific_desc' },
];

const FREQUENCY_OPTIONS = [
  { value: 'daily',   labelKey: 'tmpl.freq_daily',   helperKey: 'tmpl.freq_daily_helper'   },
  { value: 'weekly',  labelKey: 'tmpl.freq_weekly',  helperKey: 'tmpl.freq_weekly_helper'  },
  { value: 'hourly',  labelKey: 'tmpl.freq_hourly',  helperKey: 'tmpl.freq_hourly_helper'  },
  { value: 'monthly', labelKey: 'tmpl.freq_monthly', helperKey: 'tmpl.freq_monthly_helper' },
];

const ITEM_TYPES = [
  { value: 'passfail',  labelKey: 'tmpl.item_passfail'  },
  { value: 'numeric',   labelKey: 'tmpl.item_numeric'   },
  { value: 'text',      labelKey: 'tmpl.item_text'      },
  { value: 'photo',     labelKey: 'tmpl.item_photo'     },
  { value: 'signature', labelKey: 'tmpl.item_signature' },
];

const FAIL_BEHAVIOURS = [
  { value: 'none',  labelKey: 'tmpl.fail_none',  colour: 'var(--color-text-tertiary)', dotBg: '#B4B2A9'             },
  { value: 'flag',  labelKey: 'tmpl.fail_flag',  colour: '#854F0B',                    dotBg: 'var(--color-warning)' },
  { value: 'block', labelKey: 'tmpl.fail_block', colour: '#A32D2D',                    dotBg: 'var(--color-danger)'  },
];

export default function CheckTemplateBuilder() {
  const { t } = useTranslation();
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();

  const isEdit = templateId && templateId !== 'new';

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [machines, setMachines] = useState([]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState('all');
  const [categories, setCategories] = useState([]);
  const [specificMachineId, setSpecificMachineId] = useState('');
  const [estimatedMinutes, setEstimatedMinutes] = useState(5);
  const [frequency, setFrequency] = useState('daily');
  const [frequencyHours, setFrequencyHours] = useState(50);
  const [dueByTime, setDueByTime] = useState('08:00');
  const [sections, setSections] = useState([
    { id: 's1', label: t('tmpl.section_default') + ' 1', expanded: true, items: [] },
  ]);
  const [active, setActive] = useState(true);

  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const machinesSnap = await getDocs(collection(db, 'organisations', orgId, 'machines'));
        setMachines(machinesSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        if (isEdit) {
          const snap = await getDoc(doc(db, 'organisations', orgId, 'checkTemplates', templateId));
          if (snap.exists()) {
            const d = snap.data();
            setName(d.name || '');
            setDescription(d.description || '');
            setScope(d.specificMachineId ? 'specific' : (d.machineCategories?.length ? 'category' : 'all'));
            setCategories(d.machineCategories || []);
            setSpecificMachineId(d.specificMachineId || '');
            setEstimatedMinutes(d.estimatedMinutes || 5);
            setFrequency(d.frequency || 'daily');
            setFrequencyHours(d.frequencyHours || 50);
            setDueByTime(d.dueByTime || '08:00');
            setActive(d.active !== false);
            if (d.sections?.length) {
              setSections(d.sections.map((s, i) => ({
                id: 's' + i,
                label: s.label,
                expanded: true,
                items: (s.items || []).map((it, j) => ({
                  id: 's' + i + 'i' + j, ...it,
                })),
              })));
            }
          }
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, templateId, isEdit]);

  const toggleCategory = (cat) => {
    setCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const addSection = () => {
    const id = 's' + Date.now();
    setSections(prev => [...prev, { id, label: t('tmpl.section_default') + ' ' + (prev.length + 1), expanded: true, items: [] }]);
  };

  const removeSection = (sectionId) => {
    setSections(prev => prev.filter(s => s.id !== sectionId));
  };

  const updateSectionLabel = (sectionId, label) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, label } : s));
  };

  const toggleSectionExpanded = (sectionId) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, expanded: !s.expanded } : s));
  };

  const addItem = (sectionId) => {
    const itemId = 'i' + Date.now();
    const newItem = { id: itemId, label: '', type: 'passfail', failBehaviour: 'flag', required: true };
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, items: [...s.items, newItem] } : s));
    setSelectedItem({ sectionId, itemId });
  };

  const removeItem = (sectionId, itemId) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, items: s.items.filter(i => i.id !== itemId) } : s));
    if (selectedItem?.itemId === itemId) setSelectedItem(null);
  };

  const updateItem = (sectionId, itemId, updates) => {
    setSections(prev => prev.map(s => s.id === sectionId ? {
      ...s,
      items: s.items.map(i => i.id === itemId ? { ...i, ...updates } : i),
    } : s));
  };

  const getSelectedItemObj = () => {
    if (!selectedItem) return null;
    const section = sections.find(s => s.id === selectedItem.sectionId);
    if (!section) return null;
    return section.items.find(i => i.id === selectedItem.itemId);
  };

  const getDotColour = (behaviour) => {
    if (behaviour === 'block') return 'var(--color-danger)';
    if (behaviour === 'flag') return 'var(--color-warning)';
    return '#B4B2A9';
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error(t('tmpl.err_name')); return; }
    if (scope === 'category' && !categories.length) { toast.error(t('tmpl.err_category')); return; }
    if (scope === 'specific' && !specificMachineId) { toast.error(t('tmpl.err_machine')); return; }

    const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);
    if (totalItems === 0) { toast.error(t('tmpl.err_items')); return; }

    const cleanSections = sections.map(s => ({
      label: s.label,
      items: s.items.map(({ id, ...rest }) => rest),
    }));

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        machineCategories: scope === 'category' ? categories : [],
        specificMachineId: scope === 'specific' ? specificMachineId : null,
        estimatedMinutes: parseInt(estimatedMinutes) || 5,
        frequency,
        frequencyHours: frequency === 'hourly' ? parseInt(frequencyHours) || 50 : null,
        dueByTime,
        sections: cleanSections,
        active,
        updatedAt: serverTimestamp(),
      };

      if (isEdit) {
        await updateDoc(doc(db, 'organisations', orgId, 'checkTemplates', templateId), payload);
        toast.success(t('tmpl.updated'));
      } else {
        payload.createdAt = serverTimestamp();
        payload.createdBy = userProfile?.id;
        await addDoc(collection(db, 'organisations', orgId, 'checkTemplates'), payload);
        toast.success(t('tmpl.created'));
      }
      navigate('/checks');
    } catch (err) {
      console.error(err);
      toast.error(t('tmpl.err_save'));
    }
    setSaving(false);
  };

  const selectedItemObj = getSelectedItemObj();

  if (loading) return <Layout><div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div></Layout>;

  return (
    <Layout>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px', borderBottom: '0.5px solid var(--color-border-light)',
        background: 'var(--color-bg-primary)', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
          <div
            onClick={() => navigate('/checks')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer' }}
          >
            <IconArrowLeft size={15} stroke={1.5} /> {t('checks.tab_templates')}
          </div>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('tmpl.name_placeholder')}
            style={{
              flex: 1, maxWidth: 500,
              fontSize: 18, fontWeight: 400,
              border: 'none', outline: 'none',
              background: 'transparent', padding: 0,
              color: 'var(--color-text-primary)',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={() => navigate('/checks')}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            loading={saving}
            icon={<IconCheck size={14} stroke={1.5} />}
          >
            {t('tmpl.save')}
          </Button>
        </div>
      </div>

      {/* Body with right panel */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--main-padding)' }}>

          {/* Template settings */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>{t('tmpl.settings')}</div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>{t('defects.col_description')}</label>
              <input
                type="text"
                placeholder={t('tmpl.description_placeholder')}
                value={description}
                onChange={e => setDescription(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>{t('tmpl.applies_to')}</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {SCOPE_OPTIONS.map(s => (
                  <div
                    key={s.value}
                    onClick={() => setScope(s.value)}
                    style={{
                      padding: '6px 14px', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: 'pointer',
                      border: '0.5px solid',
                      borderColor: scope === s.value ? 'var(--color-brand)' : 'var(--color-border-medium)',
                      background: scope === s.value ? 'var(--color-brand-light)' : 'transparent',
                      color: scope === s.value ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {t(s.labelKey)}
                  </div>
                ))}
              </div>

              {scope === 'category' && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>{t('tmpl.select_categories_caps')}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {CATEGORIES.map(c => (
                      <div
                        key={c.value}
                        onClick={() => toggleCategory(c.value)}
                        style={{
                          padding: '5px 12px', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: 'pointer',
                          border: '0.5px solid',
                          borderColor: categories.includes(c.value) ? 'var(--color-brand)' : 'var(--color-border-medium)',
                          background: categories.includes(c.value) ? 'var(--color-brand-light)' : 'transparent',
                          color: categories.includes(c.value) ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                        }}
                      >
                        {t(c.labelKey)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {scope === 'specific' && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>{t('tmpl.select_machine_caps')}</div>
                  <select
                    value={specificMachineId}
                    onChange={e => setSpecificMachineId(e.target.value)}
                    style={{ width: '100%', maxWidth: 360, fontSize: 13, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', background: 'var(--color-bg-primary)' }}
                  >
                    <option value="">{t('tmpl.choose_machine')}</option>
                    {machines.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.makeLabel} {m.modelLabel}{m.registration ? ' · ' + m.registration : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>{t('tmpl.frequency')}</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                  {FREQUENCY_OPTIONS.map(f => (
                    <div
                      key={f.value}
                      onClick={() => setFrequency(f.value)}
                      style={{
                        padding: '8px', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: 'pointer', textAlign: 'center',
                        border: '0.5px solid',
                        borderColor: frequency === f.value ? 'var(--color-brand)' : 'var(--color-border-medium)',
                        background: frequency === f.value ? 'var(--color-brand-light)' : 'transparent',
                        color: frequency === f.value ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                      }}
                    >
                      {t(f.labelKey)}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 6 }}>
                  {t(FREQUENCY_OPTIONS.find(f => f.value === frequency)?.helperKey)}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>{t('tmpl.estimated_time')}</label>
                <input
                  type="number"
                  min="1"
                  value={estimatedMinutes}
                  onChange={e => setEstimatedMinutes(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
                {frequency === 'hourly' && (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>{t('tmpl.every_x_hours')}</label>
                    <input
                      type="number"
                      min="1"
                      value={frequencyHours}
                      onChange={e => setFrequencyHours(e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                )}
              </div>
            </div>

            {frequency !== 'hourly' && (
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                  {t('tmpl.due_by_time')} <span style={{ color: 'var(--color-text-tertiary)' }}>{t('tmpl.due_by_hint')}</span>
                </label>
                <input
                  type="time"
                  value={dueByTime}
                  onChange={e => setDueByTime(e.target.value)}
                  style={{ maxWidth: 200, boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 6 }}>
                  {t('tmpl.due_by_note')}
                </div>
              </div>
            )}
          </Card>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
            {FAIL_BEHAVIOURS.map(fb => (
              <div key={fb.value} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: fb.dotBg }} />
                {t(fb.labelKey)}
              </div>
            ))}
          </div>

          {/* Sections */}
          {sections.map(section => (
            <div
              key={section.id}
              style={{
                background: 'var(--color-bg-primary)',
                border: '0.5px solid var(--color-border-light)',
                borderRadius: 'var(--radius-lg)',
                marginBottom: 10,
                overflow: 'hidden',
              }}
            >
              {/* Section header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 14px',
                background: 'var(--color-bg-secondary)',
              }}>
                <IconGripVertical size={14} color="var(--color-text-tertiary)" stroke={1.5} style={{ cursor: 'grab' }} />
                <div onClick={() => toggleSectionExpanded(section.id)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  {section.expanded
                    ? <IconChevronDown size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                    : <IconChevronRight size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                  }
                </div>
                <input
                  type="text"
                  value={section.label}
                  onChange={e => updateSectionLabel(section.id, e.target.value)}
                  style={{
                    flex: 1, border: 'none', outline: 'none', background: 'transparent',
                    fontSize: 13, fontWeight: 400, padding: 0,
                  }}
                />
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                  {section.items.length} {t(section.items.length === 1 ? 'tmpl.item_one' : 'tmpl.item_many')}
                </span>
                <IconTrash
                  size={14}
                  color="var(--color-text-tertiary)"
                  stroke={1.5}
                  style={{ cursor: 'pointer' }}
                  onClick={() => removeSection(section.id)}
                />
              </div>

              {/* Items */}
              {section.expanded && (
                <div>
                  {section.items.map(item => {
                    const isSelected = selectedItem?.itemId === item.id;
                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelectedItem({ sectionId: section.id, itemId: item.id })}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 14px',
                          borderTop: '0.5px solid var(--color-border-light)',
                          background: isSelected ? 'var(--color-brand-light)' : 'transparent',
                          cursor: 'pointer',
                        }}
                      >
                        <IconGripVertical size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: getDotColour(item.failBehaviour), flexShrink: 0 }} />
                        <div style={{ flex: 1, fontSize: 13, color: item.label ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)' }}>
                          {item.label || t('tmpl.new_item')}
                        </div>
                        <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {t(ITEM_TYPES.find(it => it.value === item.type)?.labelKey)}
                        </span>
                        <IconTrash
                          size={13}
                          color="var(--color-text-tertiary)"
                          stroke={1.5}
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => { e.stopPropagation(); removeItem(section.id, item.id); }}
                        />
                      </div>
                    );
                  })}
                  <div
                    onClick={() => addItem(section.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '10px 14px',
                      borderTop: '0.5px solid var(--color-border-light)',
                      fontSize: 13, color: 'var(--color-text-secondary)',
                      cursor: 'pointer',
                    }}
                  >
                    <IconPlus size={13} stroke={1.5} /> {t('tmpl.add_item')}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Add section */}
          <div
            onClick={addSection}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '12px',
              border: '0.5px dashed var(--color-border-medium)',
              borderRadius: 'var(--radius-lg)',
              fontSize: 13,
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              background: 'var(--color-bg-primary)',
            }}
          >
            <IconPlus size={14} stroke={1.5} /> {t('tmpl.add_section')}
          </div>
        </div>

        {/* Right panel — Check item editor */}
        <div style={{
          width: 320, flexShrink: 0,
          borderLeft: '0.5px solid var(--color-border-light)',
          background: 'var(--color-bg-primary)',
          overflowY: 'auto',
          padding: '20px 18px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 14, fontWeight: 400 }}>{t('tmpl.item_panel_title')}</div>
            {selectedItemObj && (
              <IconX size={16} color="var(--color-text-tertiary)" stroke={1.5} style={{ cursor: 'pointer' }} onClick={() => setSelectedItem(null)} />
            )}
          </div>

          {!selectedItemObj ? (
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.6, marginTop: 10 }}>
              {t('tmpl.select_item_hint')}
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: 14, marginTop: 10 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>{t('tmpl.label')}</label>
                <input
                  type="text"
                  placeholder={t('tmpl.label_placeholder')}
                  value={selectedItemObj.label}
                  onChange={e => updateItem(selectedItem.sectionId, selectedItem.itemId, { label: e.target.value })}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>{t('service.type')}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ITEM_TYPES.map(it => (
                    <div
                      key={it.value}
                      onClick={() => updateItem(selectedItem.sectionId, selectedItem.itemId, { type: it.value })}
                      style={{
                        padding: '4px 10px', borderRadius: 'var(--radius-md)', fontSize: 11, cursor: 'pointer',
                        border: '0.5px solid',
                        borderColor: selectedItemObj.type === it.value ? 'var(--color-brand)' : 'var(--color-border-medium)',
                        background: selectedItemObj.type === it.value ? 'var(--color-brand-light)' : 'transparent',
                        color: selectedItemObj.type === it.value ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                      }}
                    >
                      {t(it.labelKey)}
                    </div>
                  ))}
                </div>
              </div>

              {selectedItemObj.type === 'passfail' && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>{t('tmpl.if_fails')}</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {FAIL_BEHAVIOURS.map(fb => (
                      <div
                        key={fb.value}
                        onClick={() => updateItem(selectedItem.sectionId, selectedItem.itemId, { failBehaviour: fb.value })}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 10px', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: 'pointer',
                          border: '0.5px solid',
                          borderColor: selectedItemObj.failBehaviour === fb.value ? fb.dotBg : 'var(--color-border-medium)',
                          background: selectedItemObj.failBehaviour === fb.value ? fb.dotBg + '18' : 'transparent',
                          color: selectedItemObj.failBehaviour === fb.value ? fb.colour : 'var(--color-text-secondary)',
                        }}
                      >
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: fb.dotBg, flexShrink: 0 }} />
                        {t(fb.labelKey)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-primary)' }}>{t('tmpl.required')}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{t('tmpl.required_hint')}</div>
                  </div>
                  <div
                    onClick={() => updateItem(selectedItem.sectionId, selectedItem.itemId, { required: !selectedItemObj.required })}
                    style={{
                      width: 36, height: 20, borderRadius: 10,
                      background: selectedItemObj.required ? 'var(--color-brand)' : 'var(--color-bg-tertiary)',
                      position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                    }}
                  >
                    <div style={{
                      width: 14, height: 14, borderRadius: '50%',
                      background: '#fff', position: 'absolute', top: 3,
                      left: selectedItemObj.required ? 19 : 3,
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                    }} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}